import { useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import { tenantApi, type InboundNotificationDetail, type NotificationInboxItem } from '../../services/tenantApi';
import { EventPayloadModal } from '../../components/EventPayloadModal';
import { formatEventPayload } from '../../components/alertEventPayload';
import { notificationEventDescription, notificationEventHeading, supportsNotificationDetail } from './notificationViewModel';
import '../../components/AlertDetail.css';
import './NotificationDetail.css';
import { NotificationEmailHistory } from './NotificationEmailHistory';

interface NotificationDetailProps {
  item: NotificationInboxItem;
  reloadToken?: number;
  isTenantAdmin?: boolean;
  canViewEmailHistory?: boolean;
  onClose: () => void;
}

export function notificationTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : formatDateTime(value);
}

export function NotificationDetail({ item, reloadToken = 0, isTenantAdmin = false, canViewEmailHistory = isTenantAdmin, onClose }: NotificationDetailProps) {
  useDateTimePreferences();
  const [result, setResult] = useState<{ id: string; detail: InboundNotificationDetail | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [payload, setPayload] = useState<{ text: string; heading: string; trigger: HTMLButtonElement } | null>(null);
  const [replayEvent, setReplayEvent] = useState<{ eventId: string; heading: string } | null>(null);
  const [replayBusy, setReplayBusy] = useState(false);
  const [replayError, setReplayError] = useState('');
  const [replayNotice, setReplayNotice] = useState('');
  const replayCancel = useRef<HTMLButtonElement>(null);
  const sequence = useRef(0);
  const supported = supportsNotificationDetail(item);

  useEffect(() => {
    const request = ++sequence.current;
    const controller = new AbortController();
    setLoading(supported);
    setError('');
    if (supported) {
      void tenantApi.getInboundNotificationDetail(item.notificationId, controller.signal).then(detail => {
        if (request === sequence.current) setResult({ id: item.notificationId, detail });
      }).catch(() => {
        if (request === sequence.current && !controller.signal.aborted) setError('Failed to load notification details. Please retry.');
      }).finally(() => {
        if (request === sequence.current) setLoading(false);
      });
    }
    return () => { sequence.current += 1; controller.abort(); };
  }, [item.notificationId, supported, reloadToken, refresh]);

  useEffect(() => { setPayload(null); }, [item.notificationId]);
  useEffect(() => {
    if (replayEvent) replayCancel.current?.focus();
  }, [replayEvent]);

  const detail = supported && result?.id === item.notificationId ? result.detail : null;
  const openPayload = (text: string, heading: string, trigger: HTMLButtonElement) => {
    setPayload({ text: formatEventPayload(text) ?? text, heading, trigger });
  };
  const replay = async () => {
    if (!replayEvent || replayBusy) return;
    setReplayBusy(true);
    setReplayError('');
    try {
      await tenantApi.replayInboundNotification(item.notificationId, replayEvent.eventId);
      setReplayEvent(null);
      setReplayNotice('Replay submitted. A new workflow run will appear in this timeline when processing begins.');
      setRefresh(value => value + 1);
    } catch (reason) {
      setReplayError(reason instanceof Error ? reason.message : 'Failed to submit notification replay.');
    } finally {
      setReplayBusy(false);
    }
  };

  return <aside className="alert-detail notification-detail" aria-label="Notification details" aria-busy={loading}>
    <header className="detail-header">
      <h2>Notification Details</h2>
      <div className="notification-detail-controls">
        <button type="button" className="notifications-icon-button" aria-label="Refresh notification details" title="Refresh notification details"
          disabled={loading || !supported} onClick={() => setRefresh(value => value + 1)}>&#8635;</button>
        <button type="button" className="notifications-icon-button" aria-label="Close notification details" title="Close notification details" onClick={onClose}>&times;</button>
      </div>
    </header>
    <div className="detail-content">
      <div className="alert-meta">
        <span className={`badge severity severity-${(detail?.severity || item.severity || 'info').toLowerCase()}`}>{detail?.severity || item.severity || 'info'}</span>
        {(detail?.status || item.status) && <span className="badge status">{detail?.status || item.status}</span>}
      </div>
      <h3 className="alert-title">{detail?.title || item.title || 'Notification'}</h3>
      {replayNotice && <p className="notification-replay-notice" role="status">{replayNotice}</p>}
      {loading && <p role="status">Loading notification details...</p>}
      {error && <p role="alert">{error}</p>}
      {!loading && !error && !detail && <p>Details not available for this notification source</p>}
      {detail && !error && <>
        <dl className="notification-detail-metadata">
          <dt>Service</dt><dd>{detail.service || 'Not provided'}</dd>
          <dt>Source</dt><dd>{detail.source || 'Not provided'}</dd>
          <dt>Integration</dt><dd>{detail.integrationName || detail.integrationId || 'Not provided'}</dd>
          <dt>Created</dt><dd>{notificationTimestamp(detail.createdAt)}</dd>
          <dt>Updated</dt><dd>{notificationTimestamp(detail.updatedAt)}</dd>
          <dt>Notification ID</dt><dd>{detail.notificationId}</dd>
        </dl>
        <section className="notification-description"><h4>Description</h4><p>{detail.description || 'No description provided.'}</p></section>
        <button type="button" className="btn-view-payload" disabled={!detail.rawPayload?.trim()} aria-haspopup="dialog"
          onClick={event => openPayload(detail.rawPayload, 'Notification Raw Data', event.currentTarget)}>Show Notification Raw Data</button>
        {detail.normalisedPayload?.trim() && <button type="button" className="btn-view-payload" aria-haspopup="dialog"
          onClick={event => openPayload(detail.normalisedPayload!, 'Notification Normalized Payload', event.currentTarget)}>Show Normalized Payload</button>}
        {canViewEmailHistory && <NotificationEmailHistory key={item.notificationId} notificationId={item.notificationId} reloadToken={reloadToken + refresh} />}
        <section className="notification-timeline" aria-label="Notification timeline">
          <h4>Timeline</h4>
          {detail.timeline.length === 0 && <p>No recorded events.</p>}
          <ol>{detail.timeline.map(event => {
            const heading = notificationEventHeading(event.eventType);
            const description = notificationEventDescription(event);
            return <li key={event.eventId} className={event.eventType.toLowerCase().includes('failed') ? 'is-failed' : ''}>
              <div className="notification-event-heading"><h5>{heading}</h5>
                {event.rawDetails?.trim() && <button type="button" className="timeline-payload-button" aria-haspopup="dialog"
                  aria-label={`View payload for ${heading}`} onClick={click => openPayload(event.rawDetails, heading, click.currentTarget)}>View Payload</button>}
                {isTenantAdmin && event.canReplay && <button type="button" className="timeline-payload-button notification-replay-button"
                  aria-label={`Replay payload for ${heading}`} onClick={() => { setReplayError(''); setReplayEvent({ eventId: event.eventId, heading }); }}>
                  Replay payload
                </button>}
              </div>
              <time dateTime={event.timestamp}>{notificationTimestamp(event.timestamp)}</time>
              {description && <p>{description}</p>}
            </li>;
          })}</ol>
        </section>
      </>}
    </div>
    {payload && <EventPayloadModal payload={payload.text} eventHeading={payload.heading} trigger={payload.trigger} onClose={() => setPayload(null)} />}
    {replayEvent && <div className="modal-overlay notification-replay-overlay"
      onClick={event => { if (!replayBusy && event.target === event.currentTarget) setReplayEvent(null); }}>
      <div className="modal modal-sm notification-replay-confirmation" role="alertdialog" aria-modal="true"
        aria-labelledby="notification-replay-title" aria-describedby="notification-replay-message"
        onKeyDown={event => {
          if (event.key === 'Escape' && !replayBusy) { event.preventDefault(); setReplayEvent(null); }
          if (event.key === 'Tab') {
            const cancel = replayCancel.current;
            const proceed = event.currentTarget.querySelector<HTMLButtonElement>('.btn-primary');
            if (event.shiftKey && document.activeElement === cancel) { event.preventDefault(); proceed?.focus(); }
            else if (!event.shiftKey && document.activeElement === proceed) { event.preventDefault(); cancel?.focus(); }
          }
        }}>
        <h3 id="notification-replay-title">Replay notification payload?</h3>
        <p id="notification-replay-message">
          This will run the selected raw payload through the integration’s current active schema and currently enabled Notification Received workflows.
          Workflow actions may run again and create duplicate external effects, such as messages, tickets, or webhooks.
          A new workflow run will be linked to this notification; its existing payload and history will not be replaced.
        </p>
        <p className="notification-replay-selected-event">Selected event: {replayEvent.heading}</p>
        {replayError && <p role="alert">{replayError}</p>}
        <div className="form-actions">
          <button ref={replayCancel} type="button" className="btn-secondary" disabled={replayBusy} onClick={() => setReplayEvent(null)}>Cancel</button>
          <button type="button" className="btn-primary" disabled={replayBusy} onClick={() => void replay()}>
            {replayBusy ? 'Submitting…' : 'Proceed'}
          </button>
        </div>
      </div>
    </div>}
  </aside>;
}